import { useSession } from '@context/sessionProvider';
import { createFetcher } from '@lib/hooks';
import { sendDataAPI } from '@lib/sendDataAPI';
import React, { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import useSWR from 'swr';

/** Pause between the trigger (a save or the app opening) and the popup. */
export const REVIEW_POPUP_DELAY_MS = 3000;

const STATUS_KEY = ['/api/review-popup/status'];

interface ReviewPopupStatus {
	canShowAfterSave: boolean;
	canShowOnAppOpen: boolean;
}

interface ReviewPopupStatusResponse {
	data: ReviewPopupStatus;
}

interface ReviewPopupContextProps {
	isOpen: boolean;
	notifyTimerSaved: () => void;
	notifyAppOpened: () => void;
	markReviewed: () => void;
	close: () => void;
}

const ReviewPopupContext = createContext<ReviewPopupContextProps | undefined>(undefined);

export function useReviewPopup(): ReviewPopupContextProps {
	const context = useContext(ReviewPopupContext);

	if (!context) {
		throw new Error('useReviewPopup must be used within a ReviewPopupProvider');
	}

	return context;
}

/**
 * Owns the App Market review popup: the server decides whether it may be shown, this provider
 * decides when. Shown at most once per page load; a failed status request just keeps it hidden.
 */
export const ReviewPopupProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
	const { context } = useSession();
	const { data, mutate } = useSWR<ReviewPopupStatusResponse>(STATUS_KEY, createFetcher(context), {
		refreshInterval: 0,
		revalidateOnFocus: false,
		shouldRetryOnError: false,
	});

	const [isOpen, setIsOpen] = useState(false);
	const [isAppOpened, setIsAppOpened] = useState(false);
	const showTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const isReviewRecorded = useRef(false);

	const status = data?.data;

	const scheduleShow = useCallback(() => {
		if (showTimer.current) {
			return;
		}

		showTimer.current = setTimeout(() => {
			isReviewRecorded.current = false;
			setIsOpen(true);
			mutate({ data: { canShowAfterSave: false, canShowOnAppOpen: false } }, false);
			sendDataAPI({ jwtToken: context }, '/api/review-popup/shown', 'POST');
		}, REVIEW_POPUP_DELAY_MS);
	}, [context, mutate]);

	useEffect(() => {
		return () => {
			if (showTimer.current) {
				clearTimeout(showTimer.current);
			}
		};
	}, []);

	useEffect(() => {
		if (isAppOpened && status?.canShowOnAppOpen) {
			scheduleShow();
		}
	}, [isAppOpened, status?.canShowOnAppOpen, scheduleShow]);

	const notifyTimerSaved = useCallback(() => {
		if (status?.canShowAfterSave) {
			scheduleShow();
		}
	}, [status?.canShowAfterSave, scheduleShow]);

	const notifyAppOpened = useCallback(() => {
		setIsAppOpened(true);
	}, []);

	const markReviewed = useCallback(() => {
		if (isReviewRecorded.current) {
			return;
		}

		isReviewRecorded.current = true;
		sendDataAPI({ jwtToken: context }, '/api/review-popup/reviewed', 'POST');
	}, [context]);

	const close = useCallback(() => {
		setIsOpen(false);
	}, []);

	return (
		<ReviewPopupContext.Provider value={{ isOpen, notifyTimerSaved, notifyAppOpened, markReviewed, close }}>
			{children}
		</ReviewPopupContext.Provider>
	);
};
